import _ from 'lodash'
import Vue from 'vue'
import { Module, Action, getModule, Mutation, VuexModule } from 'vuex-module-decorators'
import { TCWUnexpectedError, TCWBadDataStructureError } from '@/plugins/TCWBaseError'
import { MethodType, ApiAgent } from '@/plugins/ApiRequest'
import store from '@/store'
import { IFormStore, UpdatePayload } from '@/store/types'
import { sessionStore } from '@/store/session/SessionStoreModule'
import * as ApiPath from '@/tcw/constant/ApiPath'
import { ApiGeneralSinglePayload, EditableResponse } from '@/tcw/api/types'
import * as tcw from '@/tcw/utils/TcwService'
import { KeyInfo } from '@/components/common/form/types'

/**
 */
@Module({
  name: 'UserSetteiFormStore',
  store,
  namespaced: true,
  dynamic: true
})
export class UserSetteiFormStore extends VuexModule implements IFormStore<ApiGeneralSinglePayload> {
  // 編集データ
  protected editTargetData: ApiGeneralSinglePayload | null = null
  // 編集前データ保持用
  protected origin: ApiGeneralSinglePayload | null = null
  // ロックフラグ
  protected lockFlg = false

  /****************
   * Getter
   ****************/
  public get ready(): boolean {
    return this.editTargetData !== null
  }
  get isNewRecord(): boolean {
    return false
  }
  public get editData(): ApiGeneralSinglePayload {
    return this.editTargetData ? this.editTargetData : {}
  }
  public get isLock(): boolean {
    return false
  }
  public get apiPath(): { [type: string]: string } {
    return ApiPath.Paths.UserSettei
  }
  public get isChanged(): boolean {
    if (this.editTargetData === null) {
      return this.origin !== null
    }
    if (this.origin === null) {
      return this.editTargetData !== null
    }
    const destKeys = Object.keys(this.editTargetData)
    const srcKeys = Object.keys(this.origin)

    if (destKeys.length !== srcKeys.length) {
      return true
    }

    const isChanged = destKeys.some(d => {
      return this.editTargetData && this.origin ? this.editTargetData[d] !== this.origin[d] : false
    })

    return isChanged
  }

  /****************
   * Action
   ****************/

  /**
   * 空データ取得
   */
  @Action({ rawError: true })
  public async create(): Promise<void> {
    await this.fetchEmpty()
  }

  /**
   * 空データ取得
   */
  @Action({ rawError: true })
  public async fetchEmpty(): Promise<ApiGeneralSinglePayload> {
    // 使用しない処理
    return Promise.reject('許可されていない処理です')
  }

  /**
   * 編集データセット
   * @param id 編集データID
   */
  @Action({ rawError: true })
  public async fetch(keyInfo: KeyInfo): Promise<ApiGeneralSinglePayload> {
    // API実行
    try {
      const path = ApiPath.buildPath(this.apiPath.get, keyInfo)
      const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)

      console.log('API呼び出し')
      const response: EditableResponse<ApiGeneralSinglePayload> = await apiAgent.call<
        EditableResponse<ApiGeneralSinglePayload>
      >(path, 'GET')

      if (!response.payload) {
        throw new TCWBadDataStructureError('編集対象データがセットされていません')
      }

      // STOREにセットする
      const form = response.payload as ApiGeneralSinglePayload
      this.setEditTarget(form)
      this.setLockFlg(response.lock)

      // 呼び出し側に送る
      return Promise.resolve(form)
    } catch (e) {
      console.error('failure fetch form data', e)
      return Promise.reject(e)
    }
  }

  /**
   * 編集データサーバ送信
   */
  @Action({ rawError: true })
  public async submit(keyInfo: KeyInfo): Promise<ApiGeneralSinglePayload> {
    console.log('start submit')
    if (this.ready) {
      // APIパラメータを生成
      const pathBase: string = this.isNewRecord ? this.apiPath.post : this.apiPath.put
      const path = ApiPath.buildPath(pathBase, keyInfo)
      const method: MethodType = 'PUT'

      // API実行
      const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
      const response: ApiGeneralSinglePayload = await apiAgent.call<ApiGeneralSinglePayload>(
        path,
        method,
        this.editData as ApiGeneralSinglePayload
      )

      return response
    } else {
      // データがない場合はエラー
      return Promise.reject(new TCWUnexpectedError('データがセットされていません'))
    }
  }

  /**
   * 編集データサーバ送信
   */
  @Action({ rawError: true })
  public async delete(): Promise<boolean> {
    // 使用しない処理
    return Promise.reject('許可されていない処理です')
  }

  @Action({ rawError: true })
  public updateRecord(payload: UpdatePayload): void {
    this.update(payload)
  }
  @Action({ rawError: true })
  public clear(): void {
    this.setEditTarget(null)
  }

  /****************
   * Mutation
   ****************/
  @Mutation
  public update(payload: UpdatePayload): void {
    if (this.editTargetData === null) {
      throw new TCWBadDataStructureError('データがロードされていません')
    }
    Object.keys(payload).forEach((property: string) => {
      const updateValue = payload[property]
      console.debug(`update record: ${property} => ${updateValue}`)
      if (this.editTargetData != null) {
        // non-null-assertion回避
        Vue.set(this.editTargetData, property, updateValue)
      }
    })
  }
  @Mutation
  protected setEditTarget(record: ApiGeneralSinglePayload | null): void {
    this.editTargetData = record
    this.origin = _.cloneDeep(record)
  }
  @Mutation
  protected setLockFlg(lockFlg: boolean): void {
    this.lockFlg = lockFlg
  }
}

export const userSetteiFormStore = getModule(UserSetteiFormStore)
